import { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Check, Handshake, UserPlus, Heart, CreditCard, ExternalLink, QrCode } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const METAL_TEXTURE = "https://static.prod-images.emergentagent.com/jobs/02c52b1a-551f-4ab4-bb4f-fdecb1df21b7/images/2729419da2acb8824940645c3c551fa17af0507cf7d718cbd8dd62b26a305172.png";
const VET_ACTION = "https://customer-assets.emergentagent.com/job_vet-impact-platform/artifacts/wepects4_WhatsApp%20Image%202569-03-26%20at%2011.15.09.jpeg";
const QR_CODE = "https://customer-assets.emergentagent.com/job_vet-impact-platform/artifacts/0ggqap4l_WhatsApp%20Image%202569-03-26%20at%2010.38.23.jpeg";

const DONATION_AMOUNTS = [
  { id: "supporter", label: "Supporter", amount: 25 },
  { id: "advocate", label: "Advocate", amount: 50 },
  { id: "champion", label: "Champion", amount: 100 },
  { id: "hero", label: "Hero", amount: 250 },
  { id: "guardian", label: "Guardian", amount: 500 },
];

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: (i = 0) => ({ opacity: 1, y: 0, transition: { delay: i * 0.15, duration: 0.7, ease: "easeOut" } })
};

export default function GetInvolvedPage() {
  const [tiers, setTiers] = useState([]);
  const [nomForm, setNomForm] = useState({ nominator_name: "", nominator_email: "", veteran_name: "", veteran_story: "", veteran_contact: "" });
  const [donorForm, setDonorForm] = useState({ name: "", email: "", phone: "", amount: 0, message: "", form_type: "general" });
  const [subEmail, setSubEmail] = useState("");
  const [submitting, setSubmitting] = useState({});
  const [selectedDonation, setSelectedDonation] = useState("");
  const [customAmount, setCustomAmount] = useState("");
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [searchParams] = useSearchParams();
  const [sponsorInquiry, setSponsorInquiry] = useState(null);
  const [sponsorForm, setSponsorForm] = useState({ name: "", email: "", company: "", message: "" });

  const pollPaymentStatus = useCallback(async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    if (attempts >= maxAttempts) {
      setPaymentStatus("timeout");
      return;
    }
    try {
      const res = await axios.get(`${API}/payments/status/${sessionId}`);
      if (res.data.payment_status === "paid") {
        setPaymentStatus("paid");
        toast.success("Payment successful! Thank you for your donation!");
        return;
      } else if (res.data.status === "expired") {
        setPaymentStatus("expired");
        toast.error("Payment session expired.");
        return;
      }
      setPaymentStatus("processing");
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
    } catch {
      setPaymentStatus("error");
    }
  }, []);

  useEffect(() => {
    axios.get(`${API}/sponsorship-tiers`).then(r => setTiers(r.data)).catch(() => {});
    const sessionId = searchParams.get("session_id");
    const payment = searchParams.get("payment");
    if (sessionId && payment === "success") {
      setPaymentStatus("processing");
      pollPaymentStatus(sessionId);
    } else if (payment === "cancelled") {
      toast.info("Payment was cancelled.");
    }
  }, [searchParams, pollPaymentStatus]);

  const initiateStripePayment = async () => {
    const packageId = selectedDonation;
    const custom = parseFloat(customAmount);
    if (!packageId && (!custom || custom < 5)) {
      toast.error("Please select a donation amount or enter a custom amount ($5 minimum)");
      return;
    }
    setSubmitting(s => ({ ...s, stripe: true }));
    try {
      const res = await axios.post(`${API}/payments/checkout`, {
        package_id: packageId || "",
        custom_amount: packageId ? null : custom,
        origin_url: window.location.origin,
        donor_name: donorForm.name,
        donor_email: donorForm.email
      });
      if (res.data.url) {
        window.location.href = res.data.url;
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to start payment");
    }
    setSubmitting(s => ({ ...s, stripe: false }));
  };

  const submitNomination = async (e) => {
    e.preventDefault();
    setSubmitting(s => ({ ...s, nom: true }));
    try {
      await axios.post(`${API}/nominations`, nomForm);
      toast.success("Nomination submitted successfully!");
      setNomForm({ nominator_name: "", nominator_email: "", veteran_name: "", veteran_story: "", veteran_contact: "" });
    } catch {
      toast.error("Failed to submit nomination. Please try again.");
    }
    setSubmitting(s => ({ ...s, nom: false }));
  };

  const [lastDonorId, setLastDonorId] = useState(null);

  const submitDonor = async (e) => {
    e.preventDefault();
    setSubmitting(s => ({ ...s, donor: true }));
    try {
      const res = await axios.post(`${API}/donors`, donorForm);
      setLastDonorId(res.data.id);
      toast.success("Thank you for your pledge! You can download your receipt below.");
      setDonorForm({ name: "", email: "", phone: "", amount: 0, message: "", form_type: "general" });
    } catch {
      toast.error("Failed to submit. Please try again.");
    }
    setSubmitting(s => ({ ...s, donor: false }));
  };

  const downloadReceipt = (donorId) => {
    window.open(`${API}/donors/${donorId}/receipt`, "_blank");
  };

  const submitSubscriber = async (e) => {
    e.preventDefault();
    setSubmitting(s => ({ ...s, sub: true }));
    try {
      await axios.post(`${API}/subscribers`, { email: subEmail });
      toast.success("You're on the list!");
      setSubEmail("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to subscribe.");
    }
    setSubmitting(s => ({ ...s, sub: false }));
  };

  return (
    <div data-testid="get-involved-page">
      {/* ── HERO ── */}
      <section className="pt-32 pb-16 lg:pt-40 lg:pb-24 noise-overlay relative">
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div initial="hidden" animate="visible" variants={fadeUp} custom={0}>
            <p className="font-body text-xs uppercase tracking-[0.2em] text-zinc-500 font-semibold mb-4">Take Action</p>
          </motion.div>
          <motion.h1
            initial="hidden" animate="visible" variants={fadeUp} custom={1}
            className="font-heading text-5xl sm:text-6xl lg:text-[5.5rem] uppercase tracking-tighter leading-[0.9] mb-6"
          >
            Get Involved
          </motion.h1>
          <motion.p
            initial="hidden" animate="visible" variants={fadeUp} custom={2}
            className="font-body text-base lg:text-lg text-zinc-400 max-w-xl leading-relaxed"
          >
            Multiple ways to support the mission. Sponsor, partner, nominate, or donate.
          </motion.p>
        </div>
      </section>

      {/* ── SPONSORSHIP TIERS ── */}
      <section data-testid="sponsorship-section" className="py-24 lg:py-32 relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={METAL_TEXTURE} alt="" className="w-full h-full object-cover opacity-30" />
          <div className="absolute inset-0 bg-black/80" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="font-body text-xs uppercase tracking-[0.2em] text-zinc-500 font-semibold mb-4">Sponsorship</p>
            <h2 className="font-subheading text-3xl sm:text-4xl lg:text-5xl uppercase tracking-tight mb-4">
              Sponsorship Opportunities
            </h2>
            <p className="font-body text-base text-zinc-500 max-w-xl mx-auto">
              Align your brand with purpose. Every tier delivers real exposure while supporting veterans.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {tiers.map((tier, i) => (
              <motion.div
                key={tier.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.15, duration: 0.6 }}
                className={`border border-white/10 bg-black/60 backdrop-blur-sm p-8 card-hover ${i === 0 ? 'glow-border' : ''}`}
                data-testid={`sponsorship-tier-${i}`}
              >
                {i === 0 && (
                  <span className="inline-block font-body text-[10px] uppercase tracking-[0.2em] text-black bg-white px-3 py-1 mb-4">
                    Premium
                  </span>
                )}
                <span className="block font-body text-xs uppercase tracking-[0.2em] text-zinc-500 mb-1">{tier.price_range}</span>
                <h3 className="font-subheading text-2xl uppercase tracking-normal mb-4">{tier.name}</h3>
                <p className="font-body text-sm text-zinc-500 mb-6 leading-relaxed">{tier.description}</p>
                <ul className="space-y-3 mb-8">
                  {tier.benefits.map((benefit, bi) => (
                    <li key={bi} className="flex items-start gap-2">
                      <Check size={14} className="text-zinc-400 mt-0.5 shrink-0" />
                      <span className="font-body text-sm text-zinc-400">{benefit}</span>
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() => setSponsorInquiry(tier.name)}
                  data-testid={`sponsor-inquiry-${i}`}
                  className={`inline-block font-subheading text-xs uppercase tracking-widest px-6 py-2.5 transition-all duration-300 ${
                    i === 0
                      ? "bg-white text-black hover:bg-zinc-200"
                      : "border border-white/20 text-white hover:border-white hover:bg-white/5"
                  }`}
                >
                  Become a Sponsor
                </button>
              </motion.div>
            ))}
          </div>

          {/* Sponsor Inquiry Form */}
          {sponsorInquiry && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-12 border border-white/10 bg-black/60 backdrop-blur-sm p-8 max-w-lg mx-auto"
              data-testid="sponsor-inquiry-form"
            >
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-subheading text-lg uppercase tracking-wider">Sponsorship Inquiry</h3>
                <button onClick={() => setSponsorInquiry(null)} className="text-zinc-500 hover:text-white text-xs font-subheading uppercase">Close</button>
              </div>
              <p className="font-body text-sm text-zinc-500 mb-6">Interested in: <span className="text-white">{sponsorInquiry}</span></p>
              <form onSubmit={async (e) => {
                e.preventDefault();
                setSubmitting(s => ({...s, sponsor: true}));
                try {
                  await axios.post(`${API}/contacts`, {
                    name: sponsorForm.name,
                    email: sponsorForm.email,
                    message: `SPONSORSHIP INQUIRY - ${sponsorInquiry}\nCompany: ${sponsorForm.company}\n\n${sponsorForm.message}`
                  });
                  toast.success("Inquiry sent! We'll be in touch soon.");
                  setSponsorInquiry(null);
                  setSponsorForm({ name: "", email: "", company: "", message: "" });
                } catch { toast.error("Failed to send. Please try again."); }
                setSubmitting(s => ({...s, sponsor: false}));
              }} className="space-y-5">
                <input data-testid="sponsor-name" required value={sponsorForm.name} onChange={e => setSponsorForm({...sponsorForm, name: e.target.value})} className="bc-input w-full" placeholder="Your name" />
                <input data-testid="sponsor-email" type="email" required value={sponsorForm.email} onChange={e => setSponsorForm({...sponsorForm, email: e.target.value})} className="bc-input w-full" placeholder="your@email.com" />
                <input data-testid="sponsor-company" value={sponsorForm.company} onChange={e => setSponsorForm({...sponsorForm, company: e.target.value})} className="bc-input w-full" placeholder="Company / Organization" />
                <textarea data-testid="sponsor-message" rows={3} value={sponsorForm.message} onChange={e => setSponsorForm({...sponsorForm, message: e.target.value})} className="bc-input w-full resize-none" placeholder="Tell us about your interest..." />
                <button type="submit" disabled={submitting.sponsor} className="w-full font-subheading text-xs uppercase tracking-widest bg-white text-black px-6 py-3 hover:bg-zinc-200 transition-colors disabled:opacity-50">
                  {submitting.sponsor ? "Sending..." : "Send Inquiry"}
                </button>
              </form>
            </motion.div>
          )}
        </div>
      </section>

      {/* ── PARTNER WITH US ── */}
      <section className="py-24 lg:py-32 bg-zinc-950">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <div className="w-16 h-16 border border-white/20 flex items-center justify-center mb-6">
                <Handshake size={28} className="text-zinc-400" />
              </div>
              <h2 className="font-subheading text-3xl sm:text-4xl uppercase tracking-tight leading-tight mb-6">
                Partner With Us
              </h2>
              <p className="font-body text-base lg:text-lg text-zinc-400 leading-relaxed mb-6">
                We're open to collaboration with brands, media outlets, veteran organizations, and 
                anyone who shares our vision for veteran impact.
              </p>
              <ul className="space-y-3 mb-8">
                {["Content collaborations", "Event partnerships", "Media features", "Veteran organization alliances"].map((item) => (
                  <li key={item} className="flex items-center gap-2">
                    <ArrowRight size={12} className="text-zinc-500" />
                    <span className="font-body text-sm text-zinc-400">{item}</span>
                  </li>
                ))}
              </ul>
              <button
                onClick={() => setSponsorInquiry("Partnership")}
                data-testid="partner-email-link"
                className="inline-flex items-center gap-2 font-subheading text-sm uppercase tracking-widest border border-white/20 text-white px-6 py-2.5 hover:border-white hover:bg-white/5 transition-all duration-300"
              >
                Reach Out <ArrowRight size={14} />
              </button>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <div className="overflow-hidden border border-white/10">
                <video
                  autoPlay
                  loop
                  muted
                  playsInline
                  preload="auto"
                  className="w-full aspect-[4/3] object-cover"
                  data-testid="partner-video"
                >
                  <source src="https://customer-assets.emergentagent.com/job_vet-impact-platform/artifacts/vqqonoq5_WhatsApp%20Video%202569-03-26%20at%2011.29.12.mp4" type="video/mp4" />
                </video>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── NOMINATE A VETERAN ── */}
      <section data-testid="nomination-section" className="py-24 lg:py-32">
        <div className="max-w-3xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="w-16 h-16 border border-white/20 flex items-center justify-center mx-auto mb-6">
              <UserPlus size={28} className="text-zinc-400" />
            </div>
            <h2 className="font-subheading text-3xl sm:text-4xl uppercase tracking-tight mb-4">
              Nominate a Veteran
            </h2>
            <p className="font-body text-base text-zinc-500 max-w-lg mx-auto">
              Know a veteran who deserves an unforgettable experience? Tell us their story.
            </p>
          </div>
          <form onSubmit={submitNomination} className="space-y-8">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
              <div>
                <label className="font-subheading text-xs uppercase tracking-[0.15em] text-zinc-500 block mb-2">Your Name</label>
                <input
                  data-testid="nom-nominator-name"
                  required
                  value={nomForm.nominator_name}
                  onChange={e => setNomForm({ ...nomForm, nominator_name: e.target.value })}
                  className="bc-input w-full"
                  placeholder="Your full name"
                />
              </div>
              <div>
                <label className="font-subheading text-xs uppercase tracking-[0.15em] text-zinc-500 block mb-2">Your Email</label>
                <input
                  data-testid="nom-nominator-email"
                  type="email"
                  required
                  value={nomForm.nominator_email}
                  onChange={e => setNomForm({ ...nomForm, nominator_email: e.target.value })}
                  className="bc-input w-full"
                  placeholder="your@email.com"
                />
              </div>
            </div>
            <div>
              <label className="font-subheading text-xs uppercase tracking-[0.15em] text-zinc-500 block mb-2">Veteran's Name</label>
              <input
                data-testid="nom-veteran-name"
                required
                value={nomForm.veteran_name}
                onChange={e => setNomForm({ ...nomForm, veteran_name: e.target.value })}
                className="bc-input w-full"
                placeholder="Veteran's full name"
              />
            </div>
            <div>
              <label className="font-subheading text-xs uppercase tracking-[0.15em] text-zinc-500 block mb-2">Their Story</label>
              <textarea
                data-testid="nom-veteran-story"
                required
                rows={4}
                value={nomForm.veteran_story}
                onChange={e => setNomForm({ ...nomForm, veteran_story: e.target.value })}
                className="bc-input w-full resize-none"
                placeholder="Tell us about this veteran and why they deserve this experience..."
              />
            </div>
            <div>
              <label className="font-subheading text-xs uppercase tracking-[0.15em] text-zinc-500 block mb-2">Veteran's Contact (Optional)</label>
              <input
                data-testid="nom-veteran-contact"
                value={nomForm.veteran_contact}
                onChange={e => setNomForm({ ...nomForm, veteran_contact: e.target.value })}
                className="bc-input w-full"
                placeholder="Phone or email"
              />
            </div>
            <button
              type="submit"
              data-testid="nom-submit-btn"
              disabled={submitting.nom}
              className="font-subheading text-sm uppercase tracking-widest bg-white text-black px-8 py-3.5 hover:bg-zinc-200 transition-colors duration-300 disabled:opacity-50"
            >
              {submitting.nom ? "Submitting..." : "Submit Nomination"}
            </button>
          </form>
        </div>
      </section>

      {/* ── DONATE ── */}
      <section data-testid="donate-section" className="py-24 lg:py-32 bg-zinc-950">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="w-16 h-16 border border-white/20 flex items-center justify-center mx-auto mb-6">
              <Heart size={28} className="text-zinc-400" />
            </div>
            <h2 className="font-subheading text-3xl sm:text-4xl uppercase tracking-tight mb-4">
              Support The Cause
            </h2>
            <p className="font-body text-base text-zinc-500 max-w-lg mx-auto">
              Your contribution directly funds veteran experiences and content creation.
            </p>
            {paymentStatus === "paid" && (
              <div data-testid="payment-success-msg" className="mt-6 border border-green-500/30 bg-green-500/10 p-4 max-w-md mx-auto">
                <p className="font-subheading text-sm uppercase text-green-400">Payment Successful - Thank You!</p>
              </div>
            )}
            {paymentStatus === "processing" && (
              <div className="mt-6 border border-yellow-500/30 bg-yellow-500/10 p-4 max-w-md mx-auto">
                <p className="font-subheading text-sm uppercase text-yellow-400">Processing payment...</p>
              </div>
            )}
          </div>

          {/* Donation Amount Selection */}
          <div className="mb-12">
            <p className="font-subheading text-xs uppercase tracking-[0.15em] text-zinc-500 text-center mb-6">Select Amount</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-6">
              {DONATION_AMOUNTS.map((d) => (
                <button
                  key={d.id}
                  data-testid={`donation-amount-${d.id}`}
                  onClick={() => { setSelectedDonation(d.id); setCustomAmount(""); }}
                  className={`border p-4 text-center transition-all duration-300 ${
                    selectedDonation === d.id
                      ? "border-white bg-white/10 text-white"
                      : "border-white/10 text-zinc-400 hover:border-white/30"
                  }`}
                >
                  <span className="font-heading text-2xl block">${d.amount}</span>
                  <span className="font-body text-xs uppercase tracking-wider text-zinc-500">{d.label}</span>
                </button>
              ))}
            </div>
            <div className="max-w-xs mx-auto">
              <label className="font-subheading text-xs uppercase tracking-[0.15em] text-zinc-500 block mb-2 text-center">Or Custom Amount</label>
              <input
                data-testid="custom-donation-amount"
                type="number"
                min="5"
                step="0.01"
                value={customAmount}
                onChange={e => { setCustomAmount(e.target.value); setSelectedDonation(""); }}
                className="bc-input w-full text-center"
                placeholder="$5.00 minimum"
              />
            </div>
          </div>

          {/* Payment Methods */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            {/* Stripe Card Payment */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="border border-white/10 p-6 text-center card-hover"
            >
              <CreditCard size={28} className="text-zinc-400 mx-auto mb-4" />
              <h3 className="font-subheading text-lg uppercase tracking-wider mb-2">Credit / Debit Card</h3>
              <p className="font-body text-xs text-zinc-500 mb-4">Secure payment via Stripe</p>
              <button
                data-testid="stripe-pay-btn"
                onClick={initiateStripePayment}
                disabled={submitting.stripe}
                className="w-full font-subheading text-xs uppercase tracking-widest bg-white text-black px-6 py-3 hover:bg-zinc-200 transition-colors disabled:opacity-50"
              >
                {submitting.stripe ? "Redirecting..." : "Pay with Card"}
              </button>
            </motion.div>

            {/* PayPal */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="border border-white/10 p-6 text-center card-hover"
            >
              <div className="text-zinc-400 mx-auto mb-4 font-subheading text-xl tracking-wider">PayPal</div>
              <h3 className="font-subheading text-lg uppercase tracking-wider mb-2">PayPal</h3>
              <p className="font-body text-xs text-zinc-500 mb-4">Pay securely with PayPal</p>
              <a
                href="https://paypal.me/BadCompanyEnt"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="paypal-pay-btn"
                className="w-full inline-block font-subheading text-xs uppercase tracking-widest border border-white/20 text-white px-6 py-3 hover:border-white hover:bg-white/5 transition-all duration-300"
              >
                Pay with PayPal <ExternalLink size={12} className="inline ml-1" />
              </a>
            </motion.div>

            {/* Venmo */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="border border-white/10 p-6 text-center card-hover"
            >
              <div className="text-zinc-400 mx-auto mb-4 font-subheading text-xl tracking-wider">Venmo</div>
              <h3 className="font-subheading text-lg uppercase tracking-wider mb-2">Venmo</h3>
              <p className="font-body text-xs text-zinc-500 mb-4">Quick payment via Venmo</p>
              <a
                href="https://venmo.com/BadCompanyEnt"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="venmo-pay-btn"
                className="w-full inline-block font-subheading text-xs uppercase tracking-widest border border-white/20 text-white px-6 py-3 hover:border-white hover:bg-white/5 transition-all duration-300"
              >
                Pay with Venmo <ExternalLink size={12} className="inline ml-1" />
              </a>
            </motion.div>
          </div>

          {/* GoFundMe + Donor Form */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* GoFundMe QR */}
            <div className="border border-white/10 p-8 text-center">
              <QrCode size={24} className="text-zinc-400 mx-auto mb-4" />
              <h3 className="font-subheading text-lg uppercase tracking-wider mb-4">Donate on GoFundMe</h3>
              <img src={QR_CODE} alt="GoFundMe QR Code" className="w-36 h-36 mx-auto mb-4 bg-white p-1" data-testid="donate-gofundme-qr" />
              <p className="font-body text-xs text-zinc-500">Scan QR code to donate on GoFundMe</p>
            </div>

            {/* Pledge Form */}
            <div className="border border-white/10 p-8">
              <h3 className="font-subheading text-lg uppercase tracking-wider mb-6 text-center">Submit a Pledge</h3>
              <form onSubmit={submitDonor} className="space-y-5">
                <input data-testid="donor-name" required value={donorForm.name} onChange={e => setDonorForm({...donorForm, name: e.target.value})} className="bc-input w-full" placeholder="Your full name" />
                <input data-testid="donor-email" type="email" required value={donorForm.email} onChange={e => setDonorForm({...donorForm, email: e.target.value})} className="bc-input w-full" placeholder="your@email.com" />
                <input data-testid="donor-phone" value={donorForm.phone} onChange={e => setDonorForm({...donorForm, phone: e.target.value})} className="bc-input w-full" placeholder="Phone (optional)" />
                <textarea data-testid="donor-message" rows={2} value={donorForm.message} onChange={e => setDonorForm({...donorForm, message: e.target.value})} className="bc-input w-full resize-none" placeholder="Message (optional)" />
                <button type="submit" data-testid="donor-submit-btn" disabled={submitting.donor} className="w-full font-subheading text-xs uppercase tracking-widest bg-white text-black px-6 py-3 hover:bg-zinc-200 transition-colors disabled:opacity-50">
                  {submitting.donor ? "Submitting..." : "Submit Pledge"}
                </button>
                {lastDonorId && (
                  <button type="button" onClick={() => downloadReceipt(lastDonorId)} data-testid="download-receipt-btn" className="w-full font-subheading text-xs uppercase tracking-widest border border-green-500/30 text-green-400 px-6 py-3 hover:bg-green-500/10 transition-colors flex items-center justify-center gap-2 mt-3">
                    Download Donation Receipt (PDF)
                  </button>
                )}
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* ── MORE WAYS ── */}
      <section className="py-16 lg:py-20 bg-zinc-950 border-t border-white/5">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <a href="/sponsor-veteran" className="border border-white/10 p-8 card-hover block group">
              <h3 className="font-subheading text-lg uppercase tracking-wider mb-2 group-hover:text-white transition-colors">Sponsor a Veteran</h3>
              <p className="font-body text-sm text-zinc-500">Choose a veteran to sponsor directly with 100% going to their experience</p>
            </a>
            <a href="/fitness-camp" className="border border-white/10 p-8 card-hover block group">
              <h3 className="font-subheading text-lg uppercase tracking-wider mb-2 group-hover:text-white transition-colors">Veteran Fitness Camp</h3>
              <p className="font-body text-sm text-zinc-500">Discounted training programs for veterans. 3 free spots available.</p>
            </a>
          </div>
        </div>
      </section>

      {/* ── NEWSLETTER ── */}
      <section className="py-24 lg:py-32 border-t border-white/5">
        <div className="max-w-2xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="font-subheading text-3xl uppercase tracking-tight mb-4">Stay In The Loop</h2>
          <p className="font-body text-base text-zinc-500 mb-8">
            Get updates on new episodes, events, and ways to support the mission.
          </p>
          <form onSubmit={submitSubscriber} className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
            <input
              data-testid="subscribe-email"
              type="email"
              required
              value={subEmail}
              onChange={e => setSubEmail(e.target.value)}
              className="bc-input flex-1 text-center sm:text-left"
              placeholder="your@email.com"
            />
            <button
              type="submit"
              data-testid="subscribe-btn"
              disabled={submitting.sub}
              className="font-subheading text-xs uppercase tracking-widest bg-white text-black px-6 py-3 hover:bg-zinc-200 transition-colors disabled:opacity-50"
            >
              {submitting.sub ? "..." : "Subscribe"}
            </button>
          </form>
        </div>
      </section>
    </div>
  );
}
